// Externals
import { BrowserModule } from '@angular/platform-browser';
import { NgModule, LOCALE_ID } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { routing } from './app.routing';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgxCaptchaModule } from 'ngx-captcha';
import { environment } from '../environments/environment';

// Components
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginatorIntl } from '@angular/material/paginator';
import { MatChipsModule } from '@angular/material/chips';
import { getSpanishPaginatorIntl } from './config/es-paginator-intl';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatNativeDateModule, MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';

import { BcIconModule } from '@bancolombia/design-system-web/bc-icon';
import { BcInputModule } from '@bancolombia/design-system-web/bc-input';
import { BcButtonModule } from '@bancolombia/design-system-web/bc-button';
import { BcAlertModule } from '@bancolombia/design-system-web/bc-alert';
import { BcRadioModule } from '@bancolombia/design-system-web/bc-radio';
import { BcTabsModule } from '@bancolombia/design-system-web/bc-tabs-group';
import { BcCheckboxModule } from '@bancolombia/design-system-web/bc-checkbox';
import { BcModalModule } from '@bancolombia/design-system-web/bc-modal';
import { BcCarouselModule } from '@bancolombia/design-system-web/bc-carousel';

import es from '@angular/common/locales/es';
import { registerLocaleData } from '@angular/common';
registerLocaleData(es);

import {
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalRedirectComponent,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalInterceptor,
} from '@azure/msal-angular';
import {
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';

import { BcDialogService, BcServicesModule } from '@bancolombia/design-system-web/bc-services';
import { AppComponent } from './app.component';
import { HomeComponent } from './modules/base/home/components/home/home.component';
import { SharedModule } from './shared/shared.module';
import { CoreModule } from './core/core.module';
import { AuthInterceptorService } from './shared/services/auth-interceptor/auth-interceptor.service';
import { CanActivatePortalService } from './shared/services/guard/can-activate-portal.service';
import { GlobalLoaderInterceptor } from './shared/interceptors/global-loader.interceptor';
import { MSAL_SERVICE } from './shared/components/capital-user-menu/capital-user-menu.component';

export function loggerCallback(logLevel: LogLevel, message: string): void {
  console.debug(`[${logLevel}] ${message}`);
}

const publicClientApplication = new PublicClientApplication({
  auth: {
    clientId: environment.clientId,
    authority: environment.authority,
    redirectUri: environment.redirectUri,
    postLogoutRedirectUri: environment.redirectUriLogout,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    claimsBasedCachingEnabled: true,
  },
  system: {
    loggerOptions: {
      loggerCallback,
      logLevel: LogLevel.Info,
      piiLoggingEnabled: false,
    },
  },
});

export function mSALInstanceFactory(): IPublicClientApplication {
  return publicClientApplication;
}

export function mSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(environment.authority, ['application.read.all']);
  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function mSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read'],
    },
    loginFailedRoute: '/login-failed',
  };
}

const SDB_COMPONENTS = [
  BcInputModule,
  BcButtonModule,
  BcAlertModule,
  BcRadioModule,
  BcTabsModule,
  BcCheckboxModule,
  BcModalModule,
  BcCarouselModule,
  BcServicesModule,
  BcIconModule.forRoot({ path: 'https://library-sdb.apps.bancolombia.com/bds/6.25.3/' }),
];

const MATERIAL_COMPONENTS = [
  MatTableModule,
  MatSortModule,
  MatChipsModule,
  MatDialogModule,
  MatPaginatorModule,
  MatInputModule,
  MatTabsModule,
  MatFormFieldModule,
  MatSelectModule,
  MatOptionModule,
  MatIconModule,
  MatMenuModule,
  MatTooltipModule,
  MatRadioModule,
  MatAutocompleteModule,
  MatDatepickerModule,
  MatNativeDateModule,
];

@NgModule({ declarations: [AppComponent, HomeComponent],
    bootstrap: [AppComponent, MsalRedirectComponent], imports: [FormsModule,
        BrowserModule,
        ReactiveFormsModule.withConfig({ callSetDisabledState: 'whenDisabledForLegacyCode' }),
        routing,
        NgxCaptchaModule,
        SharedModule,
        CoreModule,
        MATERIAL_COMPONENTS,
        SDB_COMPONENTS,
        MsalModule], providers: [
        { provide: LOCALE_ID, useValue: 'es-ES' },
        { provide: MatPaginatorIntl, useValue: getSpanishPaginatorIntl() },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: GlobalLoaderInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptorService,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true,
        },
        CanActivatePortalService,
        MsalService,
        { provide: MSAL_SERVICE, useExisting: MsalService },
        { provide: MSAL_INSTANCE, useFactory: mSALInstanceFactory },
        { provide: MSAL_GUARD_CONFIG, useFactory: mSALGuardConfigFactory },
        { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: mSALInterceptorConfigFactory },
        MsalGuard,
        MsalBroadcastService,
        BcDialogService,
        provideHttpClient(withInterceptorsFromDi()),
    ] })
export class AppModule {}
